/**
 * Client-side discount code validation and calculation helper.
 *
 * Provides immediate validation, server verification, and subunit arithmetic
 * for promotional discount codes across reservation and checkout screens.
 */

import { types as sdkTypes } from './sdkLoader';
import { validateDiscountCodeApi } from './api';

const { Money } = sdkTypes;

/**
 * Returns active discount configuration from environment variables.
 */
export const getClientDiscountConfig = () => {
  const code = (
    process.env.REACT_APP_DISCOUNT_CODE ||
    process.env.DISCOUNT_CODE ||
    'RR20'
  ).trim();

  const percentageRaw =
    process.env.REACT_APP_DISCOUNT_PERCENTAGE ||
    process.env.DISCOUNT_PERCENTAGE ||
    '20';
  const percentage = Number.parseFloat(percentageRaw) || 20;

  const validUntilRaw =
    process.env.REACT_APP_DISCOUNT_VALID_UNTIL ||
    process.env.DISCOUNT_VALID_UNTIL ||
    '2027-12-31T23:59:59.999Z';
  const validUntil = new Date(validUntilRaw);

  return { code, percentage, validUntil };
};

/**
 * Synchronous local validation fallback for instantaneous UI checking.
 *
 * @param {string} inputCode
 * @returns {{ valid: boolean, code?: string, discountPercentage?: number, error?: string }}
 */
export const validateDiscountCodeLocally = inputCode => {
  if (!inputCode || typeof inputCode !== 'string') {
    return { valid: false, error: 'INVALID_CODE' };
  }

  const { code: activeCode, percentage, validUntil } = getClientDiscountConfig();
  const normalizedInput = inputCode.trim().toUpperCase();
  const normalizedActive = activeCode.toUpperCase();

  if (normalizedInput !== normalizedActive) {
    return { valid: false, error: 'INVALID_CODE' };
  }

  const now = new Date();
  if (!isNaN(validUntil.getTime()) && now.getTime() > validUntil.getTime()) {
    return { valid: false, error: 'EXPIRED' };
  }

  return {
    valid: true,
    code: normalizedActive,
    discountPercentage: percentage,
  };
};

/**
 * Validates discount code with backend API server, falling back to local check on network failure.
 *
 * @param {string} code - The user-provided discount code
 * @param {string|null} [email] - Customer email for checking one-time discount redemption
 * @param {string|null} [userId] - Customer Sharetribe user UUID
 * @returns {Promise<{ valid: boolean, code?: string, discountPercentage?: number, error?: string, message?: string }>}
 */
export const validateDiscountCodeAsync = async (code, email = null, userId = null) => {
  if (!code || typeof code !== 'string') {
    return { valid: false, error: 'INVALID_CODE' };
  }

  try {
    const response = await validateDiscountCodeApi(code.trim(), email, userId);
    if (response && typeof response.valid === 'boolean') {
      return response;
    }
    return validateDiscountCodeLocally(code);
  } catch (error) {
    // Graceful fallback to client-side rule when offline or network error
    return validateDiscountCodeLocally(code);
  }
};

/**
 * Calculates discount amount and discounted total for a given Money object.
 * Supports both SDK Money instances and plain { amount, currency } objects.
 *
 * @param {Money|{ amount: number, currency: string }} baseMoney - Base price Money object
 * @param {number} discountPercentage - Discount percentage, e.g. 20
 * @returns {{ originalMoney: Money, originalPrice: Money, discountMoney: Money, discountPrice: Money, discountedMoney: Money, discountedPrice: Money, discountPercentage: number } | null}
 */
export const calculateDiscount = (baseMoney, discountPercentage) => {
  const amount = baseMoney?.amount;
  const currency = baseMoney?.currency || 'USD';
  const percentage = Number(discountPercentage);

  if (typeof amount !== 'number' || isNaN(amount) || !percentage || percentage <= 0) {
    return null;
  }

  const discountAmount = Math.round(amount * (percentage / 100));
  const discountedAmount = Math.max(0, amount - discountAmount);

  const originalObj = baseMoney instanceof Money ? baseMoney : new Money(amount, currency);
  const discountObj = new Money(discountAmount, currency);
  const discountedObj = new Money(discountedAmount, currency);

  return {
    originalMoney: originalObj,
    originalPrice: originalObj,
    discountMoney: discountObj,
    discountPrice: discountObj,
    discountedMoney: discountedObj,
    discountedPrice: discountedObj,
    discountPercentage: percentage,
  };
};
